// Screenshot picker for feedback tickets (D-101): JPG/PNG/WebP only, up to three, 5 MB each.
// The Worker checks the same limits on the real bytes; this only explains them early.
import { useEffect, useId, useMemo, useState } from "react";
import { SCREENSHOT_LIMITS, SCREENSHOT_TYPES } from "../../src/feedback-tickets";
import { formatNumber, t } from "./i18n";

const MB = 1024 * 1024;

/**
 * Redraws the image so what is uploaded holds pixels only: photo metadata (location, device) is
 * dropped and an undecodable file is refused here. Keeps the original if the redraw would be
 * larger than the limit (a big PNG screenshot); the server still checks every file.
 */
async function withoutMetadata(file: File): Promise<File | null> {
  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext("2d")!.drawImage(bitmap, 0, 0);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, file.type, 0.92));
    if (!blob || blob.type !== file.type || blob.size > SCREENSHOT_LIMITS.bytes) return file.size <= SCREENSHOT_LIMITS.bytes ? file : null;
    return new File([blob], file.name, { type: file.type });
  } catch {
    return null;
  }
}

export function ScreenshotPicker({ files, onChange, disabled }: { files: File[]; onChange: (files: File[]) => void; disabled?: boolean }) {
  const inputId = useId();
  const [problem, setProblem] = useState("");
  const [preparing, setPreparing] = useState(false);
  const previews = useMemo(() => files.map((file) => URL.createObjectURL(file)), [files]);
  useEffect(() => () => previews.forEach((url) => URL.revokeObjectURL(url)), [previews]);

  async function add(list: FileList | null) {
    setProblem("");
    const picked = [...(list ?? [])];
    const typed = picked.filter((file) => (SCREENSHOT_TYPES as readonly string[]).includes(file.type) && file.size <= SCREENSHOT_LIMITS.bytes * 4);
    setPreparing(true);
    const cleaned: File[] = [];
    for (const file of typed.slice(0, SCREENSHOT_LIMITS.perMessage - files.length)) {
      const result = await withoutMetadata(file);
      if (result) cleaned.push(result);
    }
    setPreparing(false);
    const room = SCREENSHOT_LIMITS.perMessage - files.length;
    if (picked.length > room)
      setProblem(t("每則訊息最多 {count} 張截圖。", { count: SCREENSHOT_LIMITS.perMessage }));
    else if (cleaned.length < picked.length)
      setProblem(t("只能上傳 JPG、PNG 或 WebP 圖片，每張不超過 {mb} MB。", { mb: SCREENSHOT_LIMITS.bytes / MB }));
    onChange([...files, ...cleaned].slice(0, SCREENSHOT_LIMITS.perMessage));
  }

  const full = files.length >= SCREENSHOT_LIMITS.perMessage;
  const blocked = disabled || full || preparing;
  return <div className="screenshot-picker">
    <div className="screenshot-picker-head">
      <span>{t("截圖（選填）")}</span>
      <small>{t("最多 {count} 張，每張 {mb} MB 以內", { count: SCREENSHOT_LIMITS.perMessage, mb: SCREENSHOT_LIMITS.bytes / MB })}</small>
    </div>
    {files.length > 0 && <ul className="screenshot-picker-list">
      {files.map((file, index) => <li key={`${file.name}-${index}`}>
        <img src={previews[index]} alt="" />
        <span>{file.name}<small>{file.size < MB / 10 ? t("{size} KB", { size: formatNumber(Math.max(1, Math.round(file.size / 1024))) }) : t("{size} MB", { size: formatNumber(file.size / MB, { maximumFractionDigits: 1 }) })}</small></span>
        <button type="button" className="screenshot-remove" disabled={disabled} aria-label={t("移除截圖 {name}", { name: file.name })} onClick={() => onChange(files.filter((_, at) => at !== index))}>×</button>
      </li>)}
    </ul>}
    <input id={inputId} className="screenshot-input" type="file" accept={SCREENSHOT_TYPES.join(",")} multiple disabled={blocked}
      onChange={(event) => { const list = event.target.files; void add(list).finally(() => { event.target.value = ""; }); }} />
    <label htmlFor={inputId} className={`button secondary screenshot-add${blocked ? " disabled" : ""}`} aria-disabled={blocked}>{preparing ? t("處理中…") : full ? t("已達截圖上限") : t("加入截圖")}</label>
    {problem && <p className="screenshot-problem" role="alert">{problem}</p>}
  </div>;
}
